import type React from 'react';
import PropTypes from 'prop-types';
import { XIcon, InfoIcon, WarningIcon, CheckCircleIcon } from '@phosphor-icons/react';

interface IBannerProps {
  type?: 'small' | 'large';
  variant?: 'info' | 'warning' | 'alert' | 'success';
  header: string;
  body?: string;
  solid?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  showIcon?: boolean;
}
const Banner = ({
  type = 'small',
  variant = 'info',
  header,
  body,
  onClose,
  children,
  solid = true,
  showIcon = true
}: IBannerProps) => {
  let variantStyles = {
    info: {
      backgroundColor: 'bg-info', borderColor: 'border-info', textColor: 'text-white', fillColor: 'white'
    },
    warning: {
      backgroundColor: 'bg-warning', borderColor: 'border-warning', textColor: 'text-black', fillColor: 'black'
    },
    alert: {
      backgroundColor: 'bg-alert', borderColor: 'border-alert', textColor: 'text-white', fillColor: 'white'
    },
    success: {
      backgroundColor: 'bg-success', borderColor: 'border-success', textColor: 'text-white', fillColor: 'white'
    }
  };

  if (!solid) {
    variantStyles = {
      warning: {
        backgroundColor: 'bg-[#FFF6EC]', borderColor: 'border-[#FFA940]', textColor: 'text-[#212121]', fillColor: '#FFA940'
      },
      alert: {
        backgroundColor: 'bg-[#FFF6EC]', borderColor: 'border-[#FFA940]', textColor: 'text-[#212121]', fillColor: '#FFA940'
      },
      info: {
        backgroundColor: 'bg-[#E6F7FF]', borderColor: 'border-[#1890FF]', textColor: 'text-[#212121]', fillColor: '#1890FF'
      },
      success: {
        backgroundColor: 'bg-[#F6FFED]', borderColor: 'border-[#52C41A]', textColor: 'text-[#212121]', fillColor: '#52C41A'
      }

    };
  }

  const style = variantStyles[variant];

  const icons = {
    info: <InfoIcon size={16} color={style.fillColor} />,
    warning: <WarningIcon size={16} color={style.fillColor} weight="fill" />,
    alert: <WarningIcon size={16} color={style.fillColor} weight="fill" />,
    success: <CheckCircleIcon size={16} color={style.fillColor} weight="fill" />

  };

  return (
    <div className={`w-full px-4 py-3.5 ${style.backgroundColor} rounded-md border ${style.borderColor} flex`}>
      <div className='w-full flex flex-col'>
        <div className='w-full justify-between items-center flex'>
          <div className='w-full justify-start items-center gap-4 flex'>
            {showIcon && icons[variant]}
            <div className={`${style.textColor} ${type === 'large' ? 'text-base leading-6 font-semibold' : 'text-sm leading-5 font-normal'}`}>{header}</div>
          </div>
          {!!onClose
            && <XIcon size={16} color={style.fillColor} style={{ cursor: 'pointer' }} onClick={onClose} />
          }
        </div>
        {children}
        {type === 'large' && body && <div className={`flex justify-start items-center px-9 pt-2 text-sm leading-5 font-normal ${style.textColor}`}>{body}</div>}
      </div>
    </div>
  );
};

Banner.propTypes = {
  type: PropTypes.oneOf(['small', 'large']),
  variant: PropTypes.oneOf(['info', 'warning', 'alert', 'success']),
  header: PropTypes.string.isRequired,
  body: PropTypes.string,
  onClose: PropTypes.func,
  children: PropTypes.node
};

export default Banner;
